import React, { useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import WorkList from '../component/work/WorkList';
import WorkListGrid from '../component/work3/WorkList';
import { useShortCut } from '../shared/hooks/useShortCut';
import { useWorkViewStore } from '../shared/store/WorkViewStore';
import ColumnInfo from '../component/work/ColumnInfo';
import AttributedColumnInfo from '../component/work/AttributedColumnInfo';
import PageAboutWork from '../component/work/PageAboutWork';
import MobileTabletConvertWrapper from '../shared/component/MobileTabletConvertWrapper';
import ColumnInfoMobile from '../component/work/mobile/ColumnInfoMobile';
import AttributedColumnInfoMobile from '../component/work/mobile/AttributedColumnInfoMobile';
import WorkListMobile from '../component/work/mobile/WorkListMobile';
import WorkMetaStation from '../component/work/WorkMetaStation';
import { useWorkTagStore } from '../shared/store/workTagStore';
import { useWorkContentModeStore } from '../shared/store/workContentModeStore';
import { useSettingStore } from '../shared/store/settingStore';
import { isIndividualArtistArchive, shouldShowWorkArtist } from '../shared/util/workArtist';
import { DeviceType, NavLayout, WorkContentMode, WorkListType } from '../shared/enum/EnumRepository';
import { usePageOwnerIdentityStore } from '../shared/store/pageOwnerIdentityStore';
import { useWorkListStore } from '../shared/store/WorkListStore';
import { useParams } from 'react-router-dom';
import { useMemberCreditStore } from '../shared/store/memberCreditStore';
import { useWorkPropertyVisibleStore } from '../shared/store/workPropertyVisibleStore';
import { usePageStore } from '../shared/store/pageStore';
import { useNavLayout } from '../shared/hooks/useNavLayout';
import { useDeviceStore } from '../shared/store/deviceStore';
import { useEditMode } from '../shared/hooks/useEditMode';
import { useWorkFromHash } from '../shared/hooks/useWorkFromHash';

const Works: React.FC = () => {
  const setting = useSettingStore((state) => state.setting);
  const navLayout = useNavLayout();
  const deviceType = useDeviceStore((s) => s.deviceType);
  const workListType = useWorkListStore((s) => s.workListType);
  const { handleCmdE, handleCmdSlash } = useShortCut();
  const clearActiveWork = useWorkViewStore((s) => s.clearActiveWork);
  const clearSelectedTags = useWorkTagStore((s) => s.clearSelectedTags);
  const clearCreditFilter = useWorkListStore((s) => s.clearCreditFilter);
  const clearCredits = useMemberCreditStore((s) => s.clearCredits);
  const clearWorkPropertyVisible = useWorkPropertyVisibleStore(
    (s) => s.clearWorkPropertyVisible,
  );
  const resetPage = usePageStore((s) => s.resetPage);
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const { isEditMode } = useEditMode();
  useWorkFromHash();
  const setWorkContentMode = useWorkContentModeStore((s) => s.setWorkContentMode);
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const engVisible = useWorkPropertyVisibleStore(
    (s) => s.workPropertyVisible?.engVisible !== false,
  );
  const canToggleContentMode = isEditMode || engVisible;
  const isMobileView =
    deviceType === DeviceType.MOBILE || deviceType === DeviceType.TABLET;
  const isGrid =
    !isMobileView
    && isIndividualArtistArchive(setting)
    && workListType === WorkListType.GRID;

  // 목록·태그 stale은 canShow로 억제. 선택/필터·페이지 메타만 아카이브 전환 시 리셋.
  useLayoutEffect(() => {
    clearActiveWork();
    clearSelectedTags();
    clearCredits();
    clearCreditFilter();
    clearWorkPropertyVisible();
    resetPage();
  }, [raw, pageOwner?.aui]);

  /** Works 진입 시 ENG 모드가 남아 있으면 Orig로 */
  useEffect(() => {
    if (useWorkContentModeStore.getState().workContentMode === WorkContentMode.ENG) {
      setWorkContentMode(WorkContentMode.Orig);
    }
  }, [setWorkContentMode]);

  // 보기 모드에서 ENG 전환이 숨겨진 경우, 단축키로 ENG가 남지 않게 Orig로 되돌림
  useEffect(() => {
    if (canToggleContentMode) return;
    if (workContentMode === WorkContentMode.ENG) {
      setWorkContentMode(WorkContentMode.Orig);
    }
  }, [canToggleContentMode, workContentMode, setWorkContentMode]);

  return (
    <WorkContainer
      $navLayout={navLayout}
      $isGrid={isGrid}
      onKeyDown={(e) => {
        handleCmdE(e);
        if (!canToggleContentMode) return;
        handleCmdSlash(e, () => {
          setWorkContentMode(
            workContentMode === WorkContentMode.Orig
              ? WorkContentMode.ENG
              : WorkContentMode.Orig,
          );
        });
      }}
      tabIndex={-1}
    >
      {isGrid ? (
        <WorkListSlot>
          <WorkListGrid />
        </WorkListSlot>
      ) : (
        <>
          <WorkMetaStation />
          <MobileTabletConvertWrapper
            onlyMobileChildren={
              shouldShowWorkArtist(setting) ? <AttributedColumnInfoMobile /> : <ColumnInfoMobile />
            }
            onlyNotMobileChildren={
              shouldShowWorkArtist(setting) ? <AttributedColumnInfo /> : <ColumnInfo />
            }
          />
          <MobileTabletConvertWrapper
            onlyMobileChildren={<WorkListMobile />}
            onlyNotMobileChildren={<WorkList />}
          />
          <PageAboutWork />
        </>
      )}
    </WorkContainer>
  );
}

export default Works;

const WorkContainer = styled.main<{ $navLayout: NavLayout; $isGrid: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ $isGrid, $navLayout }) =>
    $isGrid
      ? $navLayout === NavLayout.FIXED
        ? `
          width: 100%;
          height: 100%;
          padding: 0 clamp(20px, 2vw, 30px) 0 clamp(12px, 1.2vw, 20px);
          margin-bottom: 0;
        `
        : `
          width: 100%;
          height: 100vh;
          padding-right: clamp(20px, 2vw, 30px);
          margin-bottom: 0;
        `
      : `
        width: 93%;
        height: 100vh;
        margin-bottom: 3vh;
      `}
`;

const WorkListSlot = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  width: 100%;
`;
